/**
 * Feature-detected camera-focus helpers over a MediaStreamTrack. Everything here
 * is a safe no-op when the device/browser does not expose the capability
 * (notably iOS Safari, which largely lacks programmatic focus control), so
 * callers never need to guard.
 */

export interface CameraCapabilitiesInfo {
  focusMode: string[];
  pointsOfInterest: boolean;
  torch: boolean;
  zoom: { min: number; max: number; step: number } | null;
  width: { min: number; max: number } | null;
  height: { min: number; max: number } | null;
}

/** A point of interest, normalised to 0..1 of the frame. */
export interface FocusPoint {
  x: number;
  y: number;
}

type AnyTrack = MediaStreamTrack & {
  getCapabilities?: () => unknown;
  applyConstraints?: (c: unknown) => Promise<void>;
};

function caps(track: MediaStreamTrack): Record<string, unknown> {
  const t = track as AnyTrack;
  try {
    return t.getCapabilities ? ((t.getCapabilities() as Record<string, unknown>) ?? {}) : {};
  } catch {
    return {};
  }
}

function range(v: unknown): { min: number; max: number } | null {
  const r = v as { min?: number; max?: number } | undefined;
  return r && typeof r.min === 'number' && typeof r.max === 'number'
    ? { min: r.min, max: r.max }
    : null;
}

/** Snapshot the track's capabilities into a plain, serialisable shape. */
export function describeCapabilities(track: MediaStreamTrack): CameraCapabilitiesInfo {
  const c = caps(track);
  const zoom = c['zoom'] as { min?: number; max?: number; step?: number } | undefined;
  return {
    focusMode: Array.isArray(c['focusMode']) ? (c['focusMode'] as string[]) : [],
    pointsOfInterest: 'pointsOfInterest' in c,
    torch: c['torch'] === true || (Array.isArray(c['torch']) && (c['torch'] as unknown[]).includes(true)),
    zoom:
      zoom && typeof zoom.min === 'number' && typeof zoom.max === 'number'
        ? { min: zoom.min, max: zoom.max, step: typeof zoom.step === 'number' ? zoom.step : 0 }
        : null,
    width: range(c['width']),
    height: range(c['height']),
  };
}

async function apply(track: MediaStreamTrack, advanced: Record<string, unknown>): Promise<void> {
  const t = track as AnyTrack;
  if (!t.applyConstraints) return;
  try {
    await t.applyConstraints({ advanced: [advanced] });
  } catch {
    /* device rejected the constraint — leave focus as-is */
  }
}

/** Ask for continuous autofocus. Returns true iff the mode is supported and applied. */
export async function applyContinuousFocus(track: MediaStreamTrack): Promise<boolean> {
  const modes = caps(track)['focusMode'];
  if (!Array.isArray(modes) || !modes.includes('continuous')) return false;
  await apply(track, { focusMode: 'continuous' });
  return true;
}

export function supportsPointsOfInterest(track: MediaStreamTrack): boolean {
  return 'pointsOfInterest' in caps(track);
}

/** Point the lens at a normalised location. No-op if unsupported. */
export async function focusAtPoint(track: MediaStreamTrack, x: number, y: number): Promise<void> {
  if (!supportsPointsOfInterest(track)) return;
  const modes = caps(track)['focusMode'];
  const mode = Array.isArray(modes) && modes.includes('continuous') ? 'continuous' : undefined;
  const advanced: Record<string, unknown> = { pointsOfInterest: [{ x, y }] };
  if (mode) advanced['focusMode'] = mode;
  await apply(track, advanced);
}

/** Convert a tap's client coords (relative to the preview rect) to a 0..1 point. */
export function tapToNormalizedPoint(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): FocusPoint {
  const x = rect.width ? (clientX - rect.left) / rect.width : 0.5;
  const y = rect.height ? (clientY - rect.top) / rect.height : 0.5;
  return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) };
}

/**
 * Decides *when* to nudge autofocus at the detected document, without hunting:
 * fires when the box centroid moves past `moveFrac`, or the frame has been
 * blurry for `stuckFrames` in a row — but never more often than `minIntervalMs`,
 * and never once the frame is crisp.
 */
export class RegionFocusController {
  private readonly moveFrac: number;
  private readonly stuckFrames: number;
  private readonly minIntervalMs: number;
  private last: FocusPoint | null = null;
  private lastFireAt = -Infinity;
  private stuckRun = 0;

  constructor(opts?: { moveFrac?: number; stuckFrames?: number; minIntervalMs?: number }) {
    this.moveFrac = opts?.moveFrac ?? 0.08;
    this.stuckFrames = opts?.stuckFrames ?? 4;
    this.minIntervalMs = opts?.minIntervalMs ?? 800;
  }

  reset(): void {
    this.last = null;
    this.lastFireAt = -Infinity;
    this.stuckRun = 0;
  }

  next(
    box: { x: number; y: number; w: number; h: number } | null,
    crisp: boolean,
    sharpBelowMin: boolean,
    now: number,
  ): FocusPoint | null {
    if (crisp) {
      this.stuckRun = 0;
      return null;
    }
    if (!box) return null;

    this.stuckRun = sharpBelowMin ? this.stuckRun + 1 : 0;

    const centroid: FocusPoint = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
    const moved =
      !this.last ||
      Math.hypot(centroid.x - this.last.x, centroid.y - this.last.y) > this.moveFrac;
    const stuck = this.stuckRun >= this.stuckFrames;

    if (!moved && !stuck) return null;
    if (now - this.lastFireAt < this.minIntervalMs) return null;

    this.last = centroid;
    this.lastFireAt = now;
    this.stuckRun = 0;
    return centroid;
  }
}
